import { useState, type ReactNode } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Trash2,
  Loader2,
  AlertCircle,
  DollarSign,
  Zap,
  CreditCard,
  RefreshCw,
  Calendar,
  Download,
  Send,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { formatCurrency, formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';
import {
  GET_INVOICE,
  GET_INVOICES_BY_ORGANIZATION,
  APPROVE_INVOICE,
  VOID_INVOICE,
  DISPUTE_INVOICE,
  DELETE_INVOICE,
  SEND_INVOICE_EMAIL,
  type InvoiceStatus,
  type GetInvoiceResponse,
} from '@/graphql/invoices';

// ==================== Status Configs ====================

const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  DRAFT: 'Draft',
  APPROVED: 'Approved',
  SENT: 'Sent',
  PAID: 'Paid',
  PARTIAL: 'Partial',
  DISPUTED: 'Disputed',
  VOID: 'Void',
};

const INVOICE_STATUS_STYLES: Record<InvoiceStatus, string> = {
  DRAFT: 'bg-pale-gray-2 text-chalkboard dark:bg-night-pond dark:text-rain-cloud',
  APPROVED: 'bg-pond/10 text-pond dark:bg-pond-light/20 dark:text-pond-light',
  SENT: 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light',
  PAID: 'bg-turquoise/10 text-turquoise dark:bg-aqua/20 dark:text-aqua',
  PARTIAL: 'bg-yellow/10 text-earth dark:bg-yellow-light/20 dark:text-yellow-light',
  DISPUTED: 'bg-rust/10 text-rust dark:bg-salmon/20 dark:text-salmon',
  VOID: 'bg-pale-gray-2 text-chalkboard/50 dark:bg-night-pond dark:text-rain-cloud/50',
};

const SOURCE_LABELS: Record<string, { label: string; style: string }> = {
  MANUAL: { label: 'Manual', style: 'bg-pale-gray-2 text-chalkboard dark:bg-night-pond dark:text-rain-cloud' },
  BLAST: { label: 'BLAST', style: 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light' },
  RECURRING: { label: 'Recurring', style: 'bg-pond/10 text-pond dark:bg-pond-light/20 dark:text-pond-light' },
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  ACH: 'ACH',
  WIRE: 'Wire Transfer',
  CHECK: 'Check',
  OTHER: 'Other',
};

// ==================== Component Props ====================

interface InvoiceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string | null;
  clientId: string;
  onRecordPayment?: (invoiceId: string) => void;
}

// ==================== Component ====================

export function InvoiceDetailModal({
  isOpen,
  onClose,
  invoiceId,
  clientId,
  onRecordPayment,
}: InvoiceDetailModalProps) {
  const [confirmAction, setConfirmAction] = useState<'void' | 'delete' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Fetch full invoice with line items + payments
  const { data, loading, error } = useQuery<GetInvoiceResponse>(GET_INVOICE, {
    variables: { id: invoiceId },
    skip: !invoiceId || !isOpen,
    fetchPolicy: 'network-only',
  });

  const invoice = data?.invoice ?? null;

  // Mutations
  const refetchConfig = {
    refetchQueries: [
      { query: GET_INVOICE, variables: { id: invoiceId } },
      { query: GET_INVOICES_BY_ORGANIZATION, variables: { organizationId: clientId } },
    ],
  };

  const [approveInvoice, { loading: approving }] = useMutation(APPROVE_INVOICE, refetchConfig);
  const [voidInvoice, { loading: voiding }] = useMutation(VOID_INVOICE, refetchConfig);
  const [disputeInvoice, { loading: disputing }] = useMutation(DISPUTE_INVOICE, refetchConfig);
  const [deleteInvoice, { loading: deleting }] = useMutation(DELETE_INVOICE, {
    refetchQueries: [
      { query: GET_INVOICES_BY_ORGANIZATION, variables: { organizationId: clientId } },
    ],
  });
  const [sendInvoiceEmail, { loading: sending }] = useMutation(SEND_INVOICE_EMAIL, refetchConfig);

  const [downloading, setDownloading] = useState(false);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);
  const isActing = approving || voiding || disputing || deleting || sending;

  // ==================== Actions ====================

  async function handleApprove() {
    if (!invoice) return;
    setActionError(null);
    try {
      await approveInvoice({ variables: { id: invoice.id } });
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to approve invoice.');
    }
  }

  async function handleVoid() {
    if (!invoice) return;
    setActionError(null);
    try {
      await voidInvoice({ variables: { id: invoice.id } });
      setConfirmAction(null);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to void invoice.');
    }
  }

  async function handleDispute() {
    if (!invoice) return;
    setActionError(null);
    try {
      await disputeInvoice({ variables: { id: invoice.id } });
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to dispute invoice.');
    }
  }

  async function handleDelete() {
    if (!invoice) return;
    setActionError(null);
    try {
      await deleteInvoice({ variables: { id: invoice.id } });
      setConfirmAction(null);
      onClose();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete invoice.');
    }
  }

  async function handleSendEmail() {
    if (!invoice) return;
    setActionError(null);
    setSendSuccess(null);
    try {
      const { data } = await sendInvoiceEmail({ variables: { id: invoice.id } });
      const result = data?.sendInvoiceEmail;
      if (result?.success) {
        setSendSuccess(`Invoice emailed to ${result.recipientEmail}`);
      } else {
        setActionError(result?.error || 'Failed to send invoice email.');
      }
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to send invoice email.');
    }
  }

  function handleRecordPayment() {
    if (!invoice || !onRecordPayment) return;
    onRecordPayment(invoice.id);
  }

  async function handleDownloadPdf() {
    if (!invoice) return;
    setDownloading(true);
    try {
      const sessionId = localStorage.getItem('sessionId');
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/v1/invoices/${invoice.id}/pdf`, {
        headers: { 'X-Session-Id': sessionId || '' },
      });
      if (!res.ok) throw new Error('Failed to download PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  }

  // ==================== Render ====================

  function renderActions() {
    if (!invoice) return null;

    const actions: ReactNode[] = [];

    switch (invoice.status) {
      case 'DRAFT':
        actions.push(
          <button
            key="approve"
            onClick={handleApprove}
            disabled={isActing}
            className="btn-primary px-3 py-1.5 text-sm flex items-center gap-1.5"
          >
            <CheckCircle2 className="w-4 h-4" />
            Approve
          </button>,
          <button
            key="delete"
            onClick={() => setConfirmAction('delete')}
            disabled={isActing}
            className="btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5 text-rust dark:text-salmon"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        );
        break;

      case 'APPROVED':
        actions.push(
          <button
            key="send"
            onClick={handleSendEmail}
            disabled={isActing}
            className="btn-primary px-3 py-1.5 text-sm flex items-center gap-1.5"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send to Client
          </button>,
          <button
            key="void"
            onClick={() => setConfirmAction('void')}
            disabled={isActing}
            className="btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5 text-rust dark:text-salmon"
          >
            <XCircle className="w-4 h-4" />
            Void
          </button>
        );
        break;

      case 'SENT':
        actions.push(
          <button
            key="payment"
            onClick={handleRecordPayment}
            disabled={isActing}
            className="btn-primary px-3 py-1.5 text-sm flex items-center gap-1.5"
          >
            <CreditCard className="w-4 h-4" />
            Record Payment
          </button>,
          <button
            key="dispute"
            onClick={handleDispute}
            disabled={isActing}
            className="btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5 text-earth dark:text-yellow-light"
          >
            <AlertTriangle className="w-4 h-4" />
            Dispute
          </button>,
          <button
            key="void"
            onClick={() => setConfirmAction('void')}
            disabled={isActing}
            className="btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5 text-rust dark:text-salmon"
          >
            <XCircle className="w-4 h-4" />
            Void
          </button>
        );
        break;

      case 'PARTIAL':
        actions.push(
          <button
            key="payment"
            onClick={handleRecordPayment}
            disabled={isActing}
            className="btn-primary px-3 py-1.5 text-sm flex items-center gap-1.5"
          >
            <CreditCard className="w-4 h-4" />
            Record Payment
          </button>,
          <button
            key="void"
            onClick={() => setConfirmAction('void')}
            disabled={isActing}
            className="btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5 text-rust dark:text-salmon"
          >
            <XCircle className="w-4 h-4" />
            Void
          </button>
        );
        break;

      case 'DISPUTED':
        actions.push(
          <button
            key="void"
            onClick={() => setConfirmAction('void')}
            disabled={isActing}
            className="btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5 text-rust dark:text-salmon"
          >
            <XCircle className="w-4 h-4" />
            Void
          </button>
        );
        break;

      // PAID, VOID — read only
    }

    return actions.length > 0 ? (
      <div className="flex items-center gap-2 flex-wrap">{actions}</div>
    ) : null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setConfirmAction(null);
        setActionError(null);
        onClose();
      }}
      title={invoice ? `Invoice ${invoice.invoiceNumber}` : 'Invoice Detail'}
      size="xl"
    >
      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-pond dark:text-pond-light" />
          <span className="ml-2 text-sm text-chalkboard dark:text-rain-cloud">
            Loading invoice...
          </span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-4 text-rust dark:text-salmon">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm">Failed to load invoice: {error.message}</p>
        </div>
      )}

      {/* Invoice Content */}
      {invoice && !loading && (
        <div className="space-y-6">
          {/* Header Info */}
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className={cn('badge text-sm', INVOICE_STATUS_STYLES[invoice.status])}>
                  {INVOICE_STATUS_LABELS[invoice.status]}
                </span>
                <span
                  className={cn(
                    'badge text-sm',
                    SOURCE_LABELS[invoice.source]?.style
                  )}
                >
                  {invoice.source === 'BLAST' && <Zap className="w-3 h-3 mr-1 inline" />}
                  {SOURCE_LABELS[invoice.source]?.label}
                </span>
              </div>
              {invoice.externalRef && !invoice.externalRef.startsWith('TMPL:') && (
                <p className="text-sm text-chalkboard/60 dark:text-rain-cloud/60">
                  Ref: {invoice.externalRef}
                </p>
              )}
            </div>
            {renderActions()}
          </div>

          {/* Recurring Info Banner */}
          {invoice.isRecurring && (
            <div className="bg-violet/5 dark:bg-violet-light/5 border border-violet/20 dark:border-violet-light/20 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <RefreshCw className="w-4 h-4 text-violet dark:text-violet-light" />
                <span className="text-sm font-medium text-violet dark:text-violet-light">
                  Recurring Template
                </span>
                {invoice.recurringSchedule && (
                  <span className="badge bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light text-xs">
                    {invoice.recurringSchedule === 'MONTHLY' ? 'Monthly' : invoice.recurringSchedule === 'QUARTERLY' ? 'Quarterly' : 'Annually'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 text-xs text-chalkboard/60 dark:text-rain-cloud/60">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Start: {invoice.recurringStartDate ? formatDate(invoice.recurringStartDate) : '—'}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  End: {invoice.recurringEndDate ? formatDate(invoice.recurringEndDate) : 'Indefinite'}
                </span>
              </div>
              <p className="text-xs text-chalkboard/50 dark:text-rain-cloud/50 mt-1.5">
                New invoices are auto-generated as drafts on the configured schedule.
              </p>
            </div>
          )}

          {/* Generated from Template Banner */}
          {invoice.source === 'RECURRING' && invoice.externalRef?.startsWith('TMPL:') && (
            <div className="bg-pond/5 dark:bg-pond-light/5 border border-pond/20 dark:border-pond-light/20 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-pond dark:text-pond-light" />
                <span className="text-sm text-chalkboard dark:text-rain-cloud">
                  Auto-generated from recurring template
                </span>
              </div>
            </div>
          )}

          {/* Send Success */}
          {sendSuccess && (
            <div className="bg-turquoise/10 dark:bg-aqua/10 border border-turquoise/20 dark:border-aqua/20 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-turquoise dark:text-aqua">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                {sendSuccess}
              </div>
            </div>
          )}

          {/* Action Error */}
          {actionError && (
            <div className="bg-rust/10 dark:bg-salmon/10 border border-rust/20 dark:border-salmon/20 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-rust dark:text-salmon">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {actionError}
              </div>
            </div>
          )}

          {/* Confirmation Dialog */}
          {confirmAction && (
            <div className="bg-rust/5 dark:bg-salmon/5 border border-rust/20 dark:border-salmon/20 rounded-lg p-4">
              <p className="text-sm font-medium text-slate dark:text-mist mb-3">
                {confirmAction === 'void'
                  ? 'Are you sure you want to void this invoice? This action cannot be undone.'
                  : 'Are you sure you want to delete this invoice? This action cannot be undone.'}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={confirmAction === 'void' ? handleVoid : handleDelete}
                  disabled={isActing}
                  className="btn-ghost px-3 py-1.5 text-sm text-rust dark:text-salmon border border-rust/30 dark:border-salmon/30"
                >
                  {isActing && <Loader2 className="w-4 h-4 mr-1.5 animate-spin inline" />}
                  Yes, {confirmAction === 'void' ? 'Void' : 'Delete'}
                </button>
                <button
                  onClick={() => setConfirmAction(null)}
                  disabled={isActing}
                  className="btn-ghost px-3 py-1.5 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-pale-gray dark:bg-puddle/20 rounded-lg p-3 text-center">
              <p className="text-xs text-chalkboard dark:text-rain-cloud mb-1">Invoice Date</p>
              <p className="text-sm font-medium text-slate dark:text-mist">
                {formatDate(invoice.invoiceDate)}
              </p>
            </div>
            <div className="bg-pale-gray dark:bg-puddle/20 rounded-lg p-3 text-center">
              <p className="text-xs text-chalkboard dark:text-rain-cloud mb-1">Due Date</p>
              <p className={cn(
                'text-sm font-medium',
                new Date(invoice.dueDate) < new Date() && !['PAID', 'VOID'].includes(invoice.status)
                  ? 'text-rust dark:text-salmon'
                  : 'text-slate dark:text-mist'
              )}>
                {formatDate(invoice.dueDate)}
              </p>
            </div>
            <div className="bg-pale-gray dark:bg-puddle/20 rounded-lg p-3 text-center">
              <p className="text-xs text-chalkboard dark:text-rain-cloud mb-1">Amount Paid</p>
              <p className="text-sm font-medium text-turquoise dark:text-aqua">
                {formatCurrency(invoice.amountPaid)}
              </p>
            </div>
            <div className="bg-pale-gray dark:bg-puddle/20 rounded-lg p-3 text-center">
              <p className="text-xs text-chalkboard dark:text-rain-cloud mb-1">Balance Due</p>
              <p className={cn(
                'text-sm font-semibold',
                invoice.balanceDue > 0
                  ? 'text-earth dark:text-yellow-light'
                  : 'text-turquoise dark:text-aqua'
              )}>
                {formatCurrency(invoice.balanceDue)}
              </p>
            </div>
          </div>

          {/* Line Items Table */}
          <div>
            <h3 className="text-sm font-medium text-slate dark:text-mist mb-3">Line Items</h3>
            <div className="card overflow-hidden">
              <table className="table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Description</th>
                    <th className="text-right">Unit Price</th>
                    <th className="text-center">Qty</th>
                    <th className="text-right">Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lineItems.map((li) => (
                    <tr key={li.id}>
                      <td className="font-mono text-sm text-chalkboard dark:text-rain-cloud">
                        {li.sku}
                      </td>
                      <td className="text-slate dark:text-mist">{li.description}</td>
                      <td className="text-right text-chalkboard dark:text-rain-cloud">
                        {formatCurrency(li.unitPrice)}
                      </td>
                      <td className="text-center text-chalkboard dark:text-rain-cloud">
                        {li.quantity}
                      </td>
                      <td className="text-right font-medium text-slate dark:text-mist">
                        {formatCurrency(li.lineTotal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray dark:border-night-pond">
                    <td colSpan={4} className="text-right font-medium text-slate dark:text-mist">
                      Subtotal
                    </td>
                    <td className="text-right font-semibold text-lg text-slate dark:text-mist">
                      {formatCurrency(invoice.subtotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Payments Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate dark:text-mist">Payments</h3>
              {['SENT', 'PARTIAL'].includes(invoice.status) && onRecordPayment && (
                <button
                  onClick={handleRecordPayment}
                  className="btn-ghost text-xs px-2.5 py-1.5 flex items-center gap-1.5 text-pond dark:text-pond-light"
                >
                  <DollarSign className="w-3.5 h-3.5" />
                  Record Payment
                </button>
              )}
            </div>

            {invoice.payments.length === 0 ? (
              <div className="border border-dashed border-gray dark:border-night-pond rounded-lg p-4 text-center">
                <p className="text-sm text-chalkboard dark:text-rain-cloud">
                  No payments recorded yet.
                </p>
              </div>
            ) : (
              <div className="card overflow-hidden">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Method</th>
                      <th>Reference</th>
                      <th className="text-right">Amount</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.payments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="text-chalkboard dark:text-rain-cloud">
                          {formatDate(payment.paymentDate)}
                        </td>
                        <td className="text-chalkboard dark:text-rain-cloud">
                          {PAYMENT_METHOD_LABELS[payment.paymentMethod] || payment.paymentMethod}
                        </td>
                        <td className="text-chalkboard dark:text-rain-cloud font-mono text-sm">
                          {payment.referenceNumber || '—'}
                        </td>
                        <td className="text-right font-medium text-turquoise dark:text-aqua">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="text-chalkboard/60 dark:text-rain-cloud/60 text-sm">
                          {payment.notes || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray dark:border-night-pond">
                      <td colSpan={3} className="text-right font-medium text-slate dark:text-mist">
                        Total Paid / Balance Due
                      </td>
                      <td className="text-right">
                        <span className="font-medium text-turquoise dark:text-aqua">
                          {formatCurrency(invoice.amountPaid)}
                        </span>
                        <span className="mx-1.5 text-chalkboard/40 dark:text-rain-cloud/40">/</span>
                        <span className={cn(
                          'font-semibold',
                          invoice.balanceDue > 0
                            ? 'text-earth dark:text-yellow-light'
                            : 'text-turquoise dark:text-aqua'
                        )}>
                          {formatCurrency(invoice.balanceDue)}
                        </span>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div>
              <h3 className="text-sm font-medium text-slate dark:text-mist mb-2">Notes</h3>
              <div className="bg-pale-gray dark:bg-puddle/20 rounded-lg p-3">
                <p className="text-sm text-chalkboard dark:text-rain-cloud whitespace-pre-wrap">
                  {invoice.notes}
                </p>
              </div>
            </div>
          )}

          {/* Download PDF */}
          <div className="flex justify-end">
            <button
              onClick={handleDownloadPdf}
              disabled={downloading}
              className="btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5 text-pond dark:text-pond-light border border-pond/20 dark:border-pond-light/20"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download PDF
            </button>
          </div>

          {/* Meta Info */}
          <div className="pt-2 border-t border-gray dark:border-night-pond">
            <div className="flex items-center gap-6 text-xs text-chalkboard/50 dark:text-rain-cloud/50">
              {invoice.createdBy && <span>Created by: {invoice.createdBy}</span>}
              <span>Created: {formatDate(invoice.createdAt)}</span>
              <span>Updated: {formatDate(invoice.updatedAt)}</span>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
